<template>
  <div class="custom-scrollbar">
    <main
      class="editor"
      :class="{
        'move-left': moveCanvasLeft,
      }"
    >
      <Header>
        <router-link
          id="back-to-pages"
          :to="{ name: 'pagelist' }"
          class="header__action"
        >
          Páginas
        </router-link>
        <router-link
          id="back-to-modals"
          :to="{ name: 'modallist' }"
          class="header__action"
        >
          <IconArrow direction="left" />
          Voltar para pop-ups
        </router-link>
        <div class="header__page-title">
          <span>{{ container.title }}</span>
          <IconLanguage
            v-if="container.hasTranslations"
            :language="container.language"
          />
        </div>
        <button
          id="notifications"
          class="header__notification"
          @click="toggleValidationPanel()"
        >
          <IconNotifications />
          <span
            v-show="hasValidationMessages"
            class="header__notification__count"
          >
            {{ validationMessagesCount }}
          </span>
        </button>
        <button
          id="publish"
          class="header__action blue-bg is-uppercase"
          :disabled="!container.isDirty || isViewerUser"
          :title="publishButtonAltText"
          @click="togglePublishingModal()"
        >
          {{ publishButtonText }}
        </button>
      </Header>

      <!-- Modal "Publicar" / "Despublicar" -->
      <div
        v-show="isInstanceBeingPublished"
        class="editor__publish-modal-modal"
        @click="togglePublishingModal()"
      >
        <div
          class="editor__publish-modal-modal__content"
          @click.stop
        >
          <div class="editor__publish-modal-modal__content__header">
            <label
              class="btn-close"
              @click="togglePublishingModal()"
            >
              <IconClose />
            </label>
            <IconRocket
              class="text-warning"
              size="lg"
            />
            <h2>Publicar</h2>
          </div>

          <div class="editor__publish-modal-modal__content__text">
            <p
              v-show="!hasValidationMessages"
              class="editor__publish-modal-modal__content__text__no-errors"
            >
              Ao <b>publicar</b>, suas alterações estarão visíveis para os seus
              usuários. Deseja continuar?
            </p>

            <p
              v-show="hasValidationMessages"
              class="editor__publish-modal-modal__content__text__has-errors"
            >
              Você tem
              <b class="has-error"
                >{{ validationMessagesCount }}
                {{
                  singularOrPlural(
                    validationMessagesCount,
                    'item pendente',
                    'itens pendentes',
                  )
                }}</b
              >
              que
              {{ singularOrPlural(validationMessagesCount, 'pode', 'podem') }}
              prejudicar a saúde da página. Revise os itens e publique
              novamente.
            </p>
          </div>

          <div
            v-show="!hasValidationMessages"
            class="editor__publish-modal-modal__content__buttons"
          >
            <button
              class="btn btn-secondary btn-white"
              type="button"
              @click="togglePublishingModal()"
            >
              Não, cancelar
            </button>
            <button
              class="btn btn-secondary"
              type="submit"
              @click="toggleGlobalInstanceStatus()"
            >
              Sim, quero publicar!
            </button>
          </div>

          <div
            v-show="hasValidationMessages"
            class="editor__publish-modal-modal__content__buttons"
          >
            <button
              class="btn btn-secondary"
              type="button"
              @click="toggleValidationPanel(true)"
            >
              Revisar
              {{
                singularOrPlural(
                  validationMessagesCount,
                  'item pendente',
                  'itens pendentes',
                )
              }}
            </button>
          </div>
        </div>
      </div>
      <div class="editor__board">
        <Canvas />
      </div>
      <InstanceProperties v-if="activeInstance || activeGlobalData" />
      <ValidationPanel v-show="showValidationPanel" />
      <EditionFooter />
    </main>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import Canvas from '@/components/Canvas.vue';
import EditionFooter from '@/components/EditionFooter.vue';
import Header from '@/components/Header.vue';
import InstanceProperties from '@/components/InstanceProperties.vue';
import ValidationPanel from '@/components/ValidationPanel.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import IconLanguage from '@/components/icons/IconLanguage.vue';
import IconNotifications from '@/components/icons/IconNotifications.vue';
import IconRocket from '@/components/icons/IconRocket.vue';

export default {
  name: 'Editor',
  components: {
    Canvas,
    EditionFooter,
    Header,
    InstanceProperties,
    ValidationPanel,
    IconClose,
    IconArrow,
    IconLanguage,
    IconNotifications,
    IconRocket,
  },
  data() {
    return {
      isInstanceBeingPublished: false,
    };
  },
  computed: {
    hasValidationMessages() {
      return this.validationMessagesCount > 0;
    },
    settingsInstance() {
      return this.container.settingsInstance;
    },
    publishButtonText() {
      return this.container.isPublished ? 'Publicar alterações' : 'Publicar';
    },
    publishButtonAltText() {
      return this.container.isDirty
        ? this.publishButtonText
        : 'Não há alterações para publicar';
    },
    moveCanvasLeft() {
      const applicableModules = [
        'WhatsappPopup',
        'CookiePopup',
        'WarningPopup',
      ];
      return (
        applicableModules.includes(this.container.module) &&
        (this.activeInstance || this.activeGlobalData)
      );
    },
    ...mapState([
      'customer',
      'customerSettings',
      'activeInstance',
      'activeGlobalData',
      'validationMessagesCount',
      'showValidationPanel',
    ]),
    ...mapGetters(['container', 'isViewerUser']),
  },
  mounted() {
    this.$store.dispatch('saveActiveContainer');

    document.body.classList.remove('no-scroll');
  },
  methods: {
    toggleSettings() {
      this.toggleActiveInstance(this.settingsInstance);
    },
    toggleGlobalInstanceStatus() {
      this.isInstanceBeingPublished = false;

      this.publishActiveContainer();
    },
    singularOrPlural(count, singular, plural) {
      if (Math.abs(count) <= 1) {
        return singular;
      }

      return plural;
    },
    togglePublishingModal() {
      this.isInstanceBeingPublished = !this.isInstanceBeingPublished;

      if (this.isInstanceBeingPublished) {
        this.validateActiveContainer();
      }
    },
    toggleValidationPanel(showValidationPanel) {
      this.validateActiveContainer();
      this.toggleValidationPanelDisplay(showValidationPanel);

      this.isInstanceBeingPublished = false;
    },
    ...mapActions([
      'toggleActiveInstance',
      'publishActiveContainer',
      'toggleValidationPanelDisplay',
      'validateActiveContainer',
    ]),
  },
};
</script>

<style lang="scss">
.published-link {
  margin: 56px 24px;

  .swal2-toast {
    padding: 8px 12px 16px 12px;
  }

  &__input {
    position: absolute;
    margin-left: -9999999px;
  }

  &__link {
    color: #545454;
    text-decoration: underline;
  }

  &__copy-btn {
    width: 20px;
    display: inline-block;
    margin-left: 12px;
    cursor: pointer;
  }
}
</style>

<style lang="scss" scoped>
.custom-scrollbar {
  height: calc(100vh - 70px);
  margin: 45px 0 0 0;
}

.editor {
  transition: padding 0.5s ease-in-out;

  &.move-left {
    padding-right: 465px;
    padding-left: 5px;
  }

  &__board {
    width: 100%;
    max-width: 1260px;
    margin: 0 auto 40px;
    transition: all 0.3s ease-in-out;

    &--open-properties {
      width: calc(100vw - 287px - 200px);
    }
  }
  &__publish-modal-modal {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9;
    &__content {
      width: 545px;
      display: flex;
      padding: 40px;
      flex-direction: column;
      background-color: $lb-white;
      align-items: center;
      &__header {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        flex-direction: column;
        position: relative;
        h2 {
          font-size: $size-bigger;
          font-weight: $lb-medium;
          line-height: 35px;
          text-align: center;
          letter-spacing: -1px;
          color: $gray-900;
          margin: 40px 0 20px 0;
        }
        .btn-close {
          position: absolute;
          top: 0;
          right: 0;
          cursor: pointer;
        }
      }

      &__text {
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;
        line-height: 26px;
        color: $gray-900;
        margin-bottom: 20px;
        &__no-errors {
          font-size: $size-normal;
        }
        &__has-errors {
          font-size: $size-small;
        }
      }

      &__buttons {
        display: flex;
        justify-content: space-evenly;
        align-items: center;
        width: 70%;
      }
    }
  }
  .toggle-settings {
    height: 40px;
    width: 135px;
    border-radius: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: box-shadow 0.3s ease-in-out;
    white-space: nowrap;
    position: fixed;
    top: 120px;
    left: 15px;
    border: initial;
    outline: none !important;
    z-index: 3;

    .icon {
      margin-right: 10px;
    }

    &:hover {
      border: 1px solid $crm-blue-hover2;
      box-shadow: 0px 5px 10px fade-out($crm-blue-hover2, 0.6);

      .icon {
        opacity: 0.8;
      }

      span {
        color: $crm-blue-primary;
      }
    }
  }
}

.is-uppercase {
  text-transform: uppercase;
}
</style>
